// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import { Camera, X } from "lucide-react";

interface Props {
  onScan: (value: string) => void;
  onClose: () => void;
  label?: string;
}

export function QrScanner({ onScan, onClose, label = "Point camera at QR code" }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    start();
    return () => stop();
  }, []);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setScanning(true);
        scan();
      }
    } catch (e: any) {
      setError("Camera not available. Please use the text input instead.");
    }
  }

  function stop() {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
  }

  async function scan() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(scan); return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    let result: string | null = null;

    // Try native BarcodeDetector first (Chrome/Android)
    if ("BarcodeDetector" in window) {
      try {
        const detector = new (window as any).BarcodeDetector({ formats: ["qr_code", "code_128", "code_39", "ean_13", "ean_8", "data_matrix"] });
        const codes = await detector.detect(canvas);
        if (codes.length > 0) result = codes[0].rawValue;
      } catch {}
    }

    // Fallback: load jsQR dynamically
    if (!result) {
      try {
        const jsQR = (await import("https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js" as any)).default;
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
        if (code) result = code.data;
      } catch {}
    }

    if (result) {
      stop();
      onScan(result);
      return;
    }
    rafRef.current = requestAnimationFrame(scan);
  }

  return (
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-dialog">
        <div className="qr-scanner-header">
          <span>{label}</span>
          <button onClick={() => { stop(); onClose(); }}><X size={18} /></button>
        </div>
        {error ? (
          <p className="qr-scanner-error">{error}</p>
        ) : (
          <div className="qr-scanner-viewfinder">
            <video ref={videoRef} autoPlay playsInline muted className="qr-scanner-video" />
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <div className="qr-scanner-reticle">
              <div className="qr-scanner-corner tl" /><div className="qr-scanner-corner tr" />
              <div className="qr-scanner-corner bl" /><div className="qr-scanner-corner br" />
            </div>
            {scanning && <p className="qr-scanner-hint">Scanning…</p>}
          </div>
        )}
      </div>
    </div>
  );
}
